import { ArrowRight } from "@phosphor-icons/react/dist/ssr";
import Image from "next/image";
import { Button } from "@/common/components-2/button";
import { SanitizedContent } from "@/common/components-2/sanitized-content";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/common/components-2/tabs";
import { cn } from "@/services/styles/cn";
import type { HowItWorksWithTabsSectionProps } from "./how-it-works-section.types";

interface TabsSectionProps {
  items: HowItWorksWithTabsSectionProps["tabs"];
}

type TabsItemProps = TabsSectionProps["items"][number]["products"][number] & {
  className?: string;
  style?: React.CSSProperties;
};

function TabsItem({ icon, title, subtitle, description, url, className, style }: TabsItemProps) {
  return (
    <a
      href={url}
      className={cn("flex min-h-[120px] items-center gap-3 rounded-2xl bg-white p-3", className)}
      style={style}
    >
      <div className="flex size-28 min-w-28 items-center justify-center rounded-xl bg-blue-200 p-3">
        <Image src={icon.url} alt={icon.alt} width={88} height={88} />
      </div>
      <div className="w-full">
        <h5 className="font-semibold text-sm lg:text-base">{title}</h5>
        <span className="text-sm">{subtitle}</span>
        <SanitizedContent as="div" content={description} className="text-muted-foreground text-xs" />
      </div>
      <Button variant="secondary" size="icon" className="size-12 min-w-12 max-md:hidden">
        <ArrowRight />
      </Button>
    </a>
  );
}

export function TabsSection({ items }: Readonly<TabsSectionProps>) {
  return (
    <Tabs className="w-full" defaultValue={items[0]?.label}>
      <TabsList className="mb-4">
        {items.map(({ label }) => (
          <TabsTrigger key={label} value={label}>
            {label}
          </TabsTrigger>
        ))}
      </TabsList>

      {items.map(({ label, products }) => (
        <TabsContent key={label} value={label} className="group flex flex-col gap-4">
          {products.map((item, index) => (
            <TabsItem
              key={item.title}
              {...item}
              className="animate-duration-[75ms] animate-ease-out group-data-[state=active]:animate-fade-left"
              style={{ animationDelay: `${index * 0.125}s` }}
            />
          ))}
        </TabsContent>
      ))}
    </Tabs>
  );
}
